import { useContext, useEffect, useState } from 'react';
import styles from './CarCard.module.css';
import Button from '../UI/Button';
import { ReactComponent as KmSVG } from '@Assets/svg/car_km.svg';
import { ReactComponent as CalendarSVG } from '@Assets/svg/calendar.svg';
import { ReactComponent as EuroSVG } from '@Assets/svg/euro.svg';
import { useTranslation } from 'react-i18next';
import Label from '../UI/Label';
import { CarDTO, CarImageDTO, CustomCarImageResourceService } from '@/openapi';
import { AuthContext } from '@/contexts/AuthContext';
import { useRemoteGet } from '@/hooks/useRemoteGet';
import { ReactComponent as AddImage } from '@Assets/svg/add_image.svg';
import { useNavigate } from 'react-router-dom';
import { formatPriceWithThousandsSeparator } from '@/utils/utils';

interface CarCardProps {
	carData: CarDTO;
	firstImageId: number | null;
	onEdit?: () => void;
	onDisable?: (carId: number) => void;
}

const CarCard = (props: CarCardProps) => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const authCtx = useContext(AuthContext);
	const isRoleAdmin = authCtx.role?.some((r) => r.name === 'ROLE_ADMIN');
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

	const [image, setImage] = useState<string | undefined>();

	const firstCarImage = useRemoteGet<CarImageDTO>({
		remoteMethod: async () => {
			if (props.firstImageId) {
				return CustomCarImageResourceService.getImage({
					imageId: props.firstImageId
				});
			}
			throw new Error('No image ID');
		},
		lazy: true
	});

	useEffect(() => {
		if (firstCarImage.data?.id === props.firstImageId) {
			setImage(`data:${firstCarImage.data.srcContentType};base64,${firstCarImage.data.src}`);
		}
	}, [firstCarImage.data, props.firstImageId]);

	useEffect(() => {
		setImage(undefined);
		if (props.firstImageId) {
			firstCarImage.fetch();
		}
	}, [props.firstImageId]);

	// DELETE function
	const toggleDeleteCar = (e?: React.MouseEvent) => {
		if (e) {
			e.stopPropagation();
		}
		setShowDeleteConfirm(!showDeleteConfirm);
	};

	// Gestisce l'eliminazione quando l'utente conferma
	const handleConfirmDelete = (e: React.MouseEvent) => {
		e.stopPropagation();
		if (props.onDisable && props.carData?.id) {
			props.onDisable(props.carData.id);
			toggleDeleteCar();
		}
	};

	return (
		<div className={styles.card}>
			{/* Card Header*/}
			<div className={styles.card_info_header}>
				<div className={styles.header_section}>
					<div className={styles.header_top}>
						<KmSVG className={styles.svg} />
						<Label className={styles.header_label} text={t('carDetails.km')} />
					</div>
					<Label className={styles.header_value} text={props.carData.km?.toString() || '0'} />
				</div>

				{props.carData.category === 'RENTAL' && (
					<>
						<div className={styles.divider} />

						<div className={styles.header_section}>
							<div className={styles.header_top}>
								<CalendarSVG className={styles.svg} />
								<Label className={styles.header_label} text={t('carDetails.months')} />
							</div>
							<Label className={styles.header_value} text={props.carData.months?.toString() || '0'} />
						</div>
					</>
				)}

				<div className={styles.divider} />

				<div className={styles.header_section}>
					<div className={styles.header_top}>
						<EuroSVG className={styles.svg} />
						<Label className={styles.header_label} text={t('carDetails.advance')} />
					</div>
					<Label className={styles.header_value} text={formatPriceWithThousandsSeparator(props.carData.advance?.toString()) || '0'} />
				</div>
			</div>

			{/* Card Image*/}
			<div className={styles.imgContainer}>
				<div className={styles.img}>
					{image ? <img className={styles.carImage} src={image} alt="car" /> : <AddImage className={styles.addImageIcon} />}
				</div>
			</div>

			{/* Card Info*/}
			<div className={styles.card_info_bottom_container}>
				<div className={styles.bottom_section}>
					<span className={styles.brand}>{props.carData.brand}</span>
					<span className={styles.model}>{props.carData.model}</span>
				</div>
				<div className={styles.bottom_section}>
					<div className={styles.bottom_top_price}>
						{props.carData.category === 'RENTAL' ? (
							<>
								<span className={styles.price}>{`€ ${props.carData.pricePerMonth}`}</span>
								<span className={styles.per_month}>{t('carDetails./month')}</span>
							</>
						) : (
							<>
								<span className={styles.price}>{`€ ${formatPriceWithThousandsSeparator(props.carData.totalPrice)}`}</span>
							</>
						)}
					</div>
					<span className={styles.vat}>{t('carDetails.iva')}</span>
				</div>
			</div>

			{/* Card Buttons*/}
			<div className={styles.card_btn_container}>
				<Button
					className={isRoleAdmin ? styles.btn_see_details : styles.btn_disable}
					name={t('carDetails.detailsBtn')}
					onClick={() => navigate(`/car/${props.carData.id}`)}
					typeSubmit={'button'}
					color="white"
					background="white"
				/>
				{isRoleAdmin && props.onEdit && (
					<Button
						className={styles.btn_modify}
						name={t('carDetails.editBtn')}
						onClick={(e) => {
							if (e) {
								e.stopPropagation();
								props.onEdit && props.onEdit();
							}
						}}
						typeSubmit={'button'}
						color="white"
						background="white"
					/>
				)}
				{isRoleAdmin && props.onDisable && (
					<Button
						className={styles.btn_disable}
						name={t('carDetails.disableBtn')}
						onClick={toggleDeleteCar}
						typeSubmit={'button'}
						color="black"
						background="white"
					/>
				)}
			</div>

			{/* Conferma eliminazione come modal nella card */}
			{isRoleAdmin && showDeleteConfirm && (
				<div className={styles.deleteConfirmModal} onClick={(e) => e.stopPropagation()}>
					<div className={styles.deleteConfirmContent}>
						<p>{t('adminPage.offersSection.deleteConfirmation')}</p>
						<div className={styles.deleteConfirmButtons}>
							<Button
								className={styles.choice_btn}
								typeSubmit="button"
								onClick={(e) => {
									if (e) handleConfirmDelete(e);
								}}
								name={t('common.yes')}
								color="white"
								background="white"
							/>
							<Button
								className={styles.choice_btn}
								typeSubmit="button"
								onClick={toggleDeleteCar}
								name={t('common.no')}
								color="black"
								background="white"
							/>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default CarCard;
